import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { finesService, errorMessage } from '../services/finesService';
import { confirmDialog } from '../ui/feedback';
import { jsonCacheService } from '../services/jsonCacheService';
import { useModal } from '../contexts/ModalContext';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../constants/permissions';
import useEjarStatus from '../hooks/useEjarStatus';
import { attentionService, attentionKey, RESOLVE_REASONS } from '../services/attentionService';
import { formatPlateNumber } from '../utils/plateFormatter';

import Layout from '../components/Layout';
import AskYelo from '../components/AskYelo';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Field, Input, Select } from '../ui/Field/Field';
import { OverviewBoardView } from './overview/OverviewBoard';
import useOverview from './overview/useOverview';
import { periodWindowText } from './overview/overview.logic';
import { PRESETS, periodOf } from './BranchCharges/branchCharges.logic';
import { Segmented } from './BranchCharges/BranchCharges';
import '../ui/ds.css';
import { dubaiHour, dubaiLongDate } from '../utils/dubaiTime';

// the Overview's branch filter (the engine's /api/overview branches)
const HOME_BRANCHES = [
    { value: 'All', label: 'All branches' }, { value: 'Airport', label: 'Airport (T1 + T2)' },
    { value: 'T1', label: 'Terminal 1' }, { value: 'T2', label: 'Terminal 2' },
    { value: 'BB', label: 'Business Bay' }, { value: 'INV', label: 'Invygo' },
];

const greeting = () => {
    const h = dubaiHour();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
};

const Dashboard = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { hasPermission } = usePermissions();
    const { showToast } = useModal();
    const { status: ejar } = useEjarStatus();

    // the module cards are their own page now (owner, 1 Oct 2026): an old link to ?tab=modules goes there
    const [params] = useSearchParams();
    useEffect(() => {
        if (params.get('tab') === 'modules') navigate('/modules', { replace: true });
    }, [params, navigate]);

    // THE Overview (owner, 1 Oct 2026): one answer from the engine for the period and branch, shown at once from
    // the browser's last copy (pages/overview/useOverview) - the same board as the KPI Dashboard's Overview
    // yesterday by default (owner, 1 Oct 2026): a whole day, 00:00:00 to 23:59:59 Dubai - exactly 24 hours
    const [preset, setPreset] = useState('yesterday');
    const [range, setRange] = useState(() => periodOf('yesterday'));
    const [branch, setBranch] = useState('All');
    const pickPreset = (id) => {
        setPreset(id);
        if (id !== 'custom') setRange(periodOf(id));
    };
    const filters = useMemo(() => ({ from: range.from, to: range.to, branch }), [range.from, range.to, branch]);
    const overview = useOverview(filters);

    // Needs attention: the expired cars one by one, and the cases someone closed by hand
    // the expired registrations, one case each (closed by hand under the fleet's own expiry text, as before)
    const expiredCars = useMemo(() => (overview.data?.fleet?.regExpired || []).map((x) => ({
        plate_number: x.plate, registration_expiry: x.expiryText || x.expiry, make: x.make, model: x.model, status: x.status,
    })), [overview.data]);
    const [resolved, setResolved] = useState({});
    const [openCase, setOpenCase] = useState(null);      // which attention item shows its cases
    const [picking, setPicking] = useState(null);        // the case whose reason is being picked
    const [showClosed, setShowClosed] = useState(false);
    const [resolvedError, setResolvedError] = useState(false);   // the closed-by-hand cases could not be read
    const askedKeys = useRef(new Set());                          // case keys already looked up in attention_resolved
    // the engine's own self-checks (Darb token, Salik, Ejar sync, backups...), live every 2 min
    const canEngine = hasPermission(PERMISSIONS.view_engine);
    // "Fix now" is the same act as on The Engine page: its own permission there and here (permissions phase 3, 4 Oct
    // 2026 - the Dashboard offered every fix to whoever saw the engine checks; the server asks it too)
    const canFix = hasPermission('engine_health_fix');
    const [engineChecks, setEngineChecks] = useState([]);
    const [checksFailed, setChecksFailed] = useState(false);     // the engine could not be asked: never "engine healthy"
    const [fixingCheck, setFixingCheck] = useState('');
    const loadChecks = useCallback((fresh = false) => {
        if (!canEngine) return;
        finesService.healthChecks(fresh)
            .then((d) => {
                setEngineChecks((d?.checks || []).filter((c) => c.level !== 'ok'));
                setChecksFailed(false);
            })
            .catch((e) => {
                logger.warn('engine checks not readable', e);
                setChecksFailed(true);
            });
    }, [canEngine]);
    useEffect(() => {
        loadChecks();
        const t = setInterval(() => loadChecks(), 120000);
        return () => clearInterval(t);
    }, [loadChecks]);

    const [refreshing, setRefreshing] = useState(false);
    const refreshingRef = useRef(false);
    const refreshCache = useCallback(async () => {
        if (refreshingRef.current) return;
        refreshingRef.current = true;
        setRefreshing(true);
        try {
            await jsonCacheService.clearCache();
            showToast('Refreshing latest data…', 'info');
            setTimeout(() => window.location.reload(), 400);
        } catch (e) {
            refreshingRef.current = false;
            setRefreshing(false);
            showToast('Could not refresh cache', 'error');
        }
    }, [showToast]);


    useEffect(() => {
        document.title = 'Overview · Yelo Dubai';
    }, []);

    // attention_resolved may be written by the permissions its rule allows (view_fleet_list,
    // write_fleet): the "Resolved" buttons only show to them (ADM-23: they showed to everyone
    // and failed with "not allowed"). The role name 'manager' no longer opens it (ADM-12).
    // its own permission since permissions phase 4 (4 Oct 2026; it followed view_fleet_list / write_fleet until the upgrade)
    const canResolve = hasPermission('dashboard_resolve_alerts');

    const canAccess = useCallback((moduleId) => {
        if (!moduleId) {
            logger.warn('canAccess called without moduleId - Access Denied (Fail-Closed)');
            return false;
        }
        return hasPermission(moduleId);
    }, [hasPermission]);

    // --- Derived view state ---

    // --- Needs attention: every case can be closed by hand (fixed or not: a stolen car, a legal case) ---
    const carCaseKey = (c) => attentionKey('car-expired', c.plate_number, c.registration_expiry);
    const openExpired = expiredCars.filter((c) => !resolved[carCaseKey(c)]);
    const ejarKey = ejar && ejar.last_error ? attentionKey('ejar-sync', ejar.last_run || ejar.last_error) : null;
    const closeCase = async (key, kind, label, reason) => {
        const by = currentUser?.name || currentUser?.email || '';
        try {
            await attentionService.resolve(key, { kind, label, reason, by });
            setResolved((m) => ({ ...m, [key]: { id: key, kind, label, reason, resolved_by: by } }));
            setPicking(null);
            showToast(`${label} · closed (${reason})`, 'success');
        } catch (e) {
            logger.error('Could not close the case', e);
            showToast(e?.code === 'permission-denied' ? 'Not allowed to close cases yet (database rules)' : 'Could not close the case', 'error');
        }
    };
    const reopenCase = async (key) => {
        try {
            await attentionService.reopen(key);
            setResolved((m) => { const n = { ...m }; delete n[key]; return n; });
        } catch (e) {
            showToast('Could not reopen the case', 'error');
        }
    };
    const carLabel = (c) => formatPlateNumber(c.plate_number) || c.plate_number || '—';
    const expiredCases = openExpired.map((c) => ({
        key: carCaseKey(c),
        kind: 'car-expired',
        label: carLabel(c),
        sub: [[c.make, c.model].filter(Boolean).join(' '), `expired ${c.registration_expiry}`, c.status].filter(Boolean).join(' · '),
        open: () => navigate(`/fleet?search=${encodeURIComponent(c.plate_number || '')}`),
    }));

    const attention = [];
    if (expiredCases.length > 0 && canAccess(PERMISSIONS.view_fleet_list)) {
        attention.push({
            id: 'expired',
            tone: 'danger',
            icon: ICONS.expired,
            title: `${expiredCases.length} vehicle registration${expiredCases.length === 1 ? '' : 's'} expired`,
            text: 'Renew with RTA, or close a case that cannot be renewed (stolen, legal case)',
            cases: expiredCases,
        });
    }
    if (ejarKey && !resolved[ejarKey]) {
        attention.push({
            id: 'ejar',
            tone: 'danger',
            icon: ICONS.refresh,
            title: 'Ejar sync failed',
            text: String(ejar.last_error).slice(0, 120),
            action: (
                <>
                    <Button size="sm" onClick={refreshCache} isLoading={refreshing}>Refresh</Button>
                    {canResolve && <Button size="sm" variant="ghost" onClick={() => closeCase(ejarKey, 'ejar-sync', 'Ejar sync error', 'Other')}>Resolved</Button>}
                </>
            ),
        });
    }
    // engine problems: fixed from here, or closed by hand (a new occurrence shows again)
    const fixCheck = async (c) => {
        if (!(await confirmDialog({ title: `${c.fix}?`, message: `${c.title}: the engine runs "${c.fix}" now.`, confirmLabel: `Yes, ${String(c.fix).toLowerCase()}` }))) return;
        setFixingCheck(c.id);
        try {
            const r = await finesService.healthFix(c.id);
            showToast(`${c.fix}: ${r?.message || 'started'}`, 'success');
            setTimeout(() => loadChecks(true), 8000);
        } catch (e) {
            showToast(`${c.fix} failed: ${errorMessage(e)}`, 'error');
        } finally {
            setFixingCheck('');
        }
    };
    const engineKey = (c) => attentionKey('engine', c.id, c.level, c.since || '');
    const isDarbToken = (c) => c.id === 'darb_session';
    engineChecks.forEach((c) => {
        const key = engineKey(c);
        if (resolved[key]) return;
        attention.push({
            id: `engine-${c.id}`,
            tone: c.level === 'error' ? 'danger' : 'warning',
            icon: ICONS.engine,
            title: isDarbToken(c) ? 'Darb token needs renewing' : c.title,
            text: isDarbToken(c) ? 'Darb rejected the saved session: sign in to Darb and paste a new token' : (c.detail || c.title),
            action: (
                <>
                    {isDarbToken(c) ? (
                        <Button size="sm" onClick={() => navigate('/the-engine?tab=toll')}>New token</Button>
                    ) : c.fix && canFix ? (
                        <Button size="sm" isLoading={fixingCheck === c.id} onClick={() => fixCheck(c)}>{c.fix}</Button>
                    ) : (
                        <Button size="sm" onClick={() => navigate('/the-engine')}>Open engine</Button>
                    )}
                    {canResolve && <Button size="sm" variant="ghost" onClick={() => closeCase(key, 'engine', c.title, 'Other')}>Resolved</Button>}
                </>
            ),
        });
    });
    // a list that could not be read must not look like "nothing to do" (ADM-15)
    if (checksFailed && canEngine) {
        attention.push({
            id: 'engine-unreachable',
            tone: 'warning',
            icon: ICONS.engine,
            title: 'Engine checks could not be loaded',
            text: 'The engine did not answer: Darb, Salik, Ejar sync and backup checks are unknown right now',
            action: <Button size="sm" onClick={() => loadChecks(true)}>Try again</Button>,
        });
    }
    // the Ejar item above duplicates the engine's own Ejar check when both are failing
    const shownAttention = attention.filter((a) => !(a.id === 'ejar' && attention.some((x) => x.id === 'engine-ejar_sync')));

    // closed by hand AND still failing (the rest are history)
    const liveKeys = new Set([
        ...expiredCars.map(carCaseKey),
        ...engineChecks.map(engineKey),
        ...(ejarKey ? [ejarKey] : []),
    ]);
    const closedList = Object.values(resolved).filter((r) => liveKeys.has(r.id));

    // Only the cases on screen are looked up (30 keys per query), and each key once: the
    // collection keeps every case ever closed, and was read whole on every open (ADM-23).
    const liveKeyList = [...liveKeys].sort().join('|');
    useEffect(() => {
        const keys = liveKeyList ? liveKeyList.split('|') : [];
        if (keys.length === 0) return;
        const asking = keys.filter((k) => !askedKeys.current.has(k));
        if (asking.length === 0) return;
        asking.forEach((k) => askedKeys.current.add(k));
        attentionService.resolvedFor(asking)
            .then((found) => { setResolvedError(false); setResolved((m) => ({ ...found, ...m })); })
            .catch((e) => {
                asking.forEach((k) => askedKeys.current.delete(k));
                logger.warn('attention_resolved not readable', e);
                setResolvedError(true);
            });
    }, [liveKeyList]);

    const firstName = (currentUser?.name || '').split(' ')[0];
    const today = dubaiLongDate();

    // The Overview's "Needs attention" (owner, 1 Oct 2026): one list - the engine's own checks, Ejar, the expired
    // registrations (closed by hand case by case) and the Overview's items (fines, overdue contracts, registrations
    // expiring this week, a part that could not be read). The Overview's own expired / Ejar items are the ones above.
    const attentionCard = (boardItems, onAction) => {
        const list = [...shownAttention, ...boardItems
            .filter((a) => a.id !== 'reg-expired' && a.id !== 'ejar')
            .map((a) => ({
                id: `ov-${a.id}`, tone: a.tone, icon: a.icon, title: a.title, text: a.sub,
                action: a.action ? <Button size="sm" onClick={() => onAction(a.action)}>{a.action.label}</Button> : null,
            }))];
        return (
                    <Card
                        padding="none"
                        title="Needs attention"
                        subtitle={[
                            list.length ? `${list.length} item${list.length === 1 ? '' : 's'}` : '',
                            resolvedError ? 'cases closed by hand could not be loaded' : '',
                        ].filter(Boolean).join(' · ') || undefined}
                    >
                        {list.length === 0 ? (
                            <div className="dash-clear">
                                <span className="dash-attn__icon dash-attn__icon--success"><Icon glyph={ICONS.check} size="sm" /></span>
                                <span className="dash-clear__text">All clear · registrations valid{canEngine ? ', engine healthy' : ''}</span>
                            </div>
                        ) : (
                            <ul className="dash-attn">
                                {list.map((a) => (
                                    <li key={a.id} className="dash-attn__group">
                                        <div className="dash-attn__item">
                                            <span className={`dash-attn__icon dash-attn__icon--${a.tone}`}><Icon glyph={a.icon} size="sm" /></span>
                                            <span className="dash-attn__text">
                                                <span className="dash-attn__title">{a.title}</span>
                                                <span className="dash-attn__sub">{a.text}</span>
                                            </span>
                                            <span className="dash-attn__actions">
                                                {a.action}
                                                {a.cases && (
                                                    <Button size="sm" aria-expanded={openCase === a.id}
                                                        onClick={() => { setOpenCase((v) => (v === a.id ? null : a.id)); setPicking(null); }}>
                                                        {openCase === a.id ? 'Hide' : 'Review'}
                                                    </Button>
                                                )}
                                            </span>
                                        </div>
                                        {a.cases && openCase === a.id && (
                                            <ul className="dash-case-list">
                                                {a.cases.map((c) => (
                                                    <li key={c.key} className="dash-case">
                                                        <span className="dash-case__text">
                                                            <span className="dash-case__title">{c.label}</span>
                                                            <span className="dash-attn__sub">{c.sub}</span>
                                                        </span>
                                                        {picking === c.key ? (
                                                            <span className="dash-case__reasons" role="group" aria-label={`Why is ${c.label} closed?`}>
                                                                {RESOLVE_REASONS.map((r) => (
                                                                    <button key={r} type="button" className="yelo-badge yelo-badge--neutral ds-chip"
                                                                        onClick={() => closeCase(c.key, c.kind, c.label, r)}>{r}</button>
                                                                ))}
                                                                <Button size="sm" variant="ghost" onClick={() => setPicking(null)}>Cancel</Button>
                                                            </span>
                                                        ) : (
                                                            <span className="dash-attn__actions">
                                                                <Button size="sm" variant="ghost" onClick={c.open}>Open</Button>
                                                                {canResolve && <Button size="sm" leadingIcon={<Icon glyph={ICONS.check} size="sm" />} onClick={() => setPicking(c.key)}>Resolved</Button>}
                                                            </span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {closedList.length > 0 && (
                            <div className="dash-closed">
                                <button type="button" className="dash-closed__toggle" aria-expanded={showClosed} onClick={() => setShowClosed((v) => !v)}>
                                    {closedList.length} closed by hand · {showClosed ? 'hide' : 'show'}
                                </button>
                                {showClosed && (
                                    <ul className="dash-case-list">
                                        {closedList.map((r) => (
                                            <li key={r.id} className="dash-case">
                                                <span className="dash-case__text">
                                                    <span className="dash-case__title">{r.label}</span>
                                                    <span className="dash-attn__sub">{r.reason}{r.resolved_by ? ` · by ${r.resolved_by}` : ''}</span>
                                                </span>
                                                {canResolve && <Button size="sm" variant="ghost" onClick={() => reopenCase(r.id)}>Reopen</Button>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </Card>
        );
    };

    return (
        <Layout
            title="Overview"
            subtitle={`${greeting()}${firstName ? `, ${firstName}` : ''} · ${today}`}
            actions={(
                <Button
                    leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}
                    onClick={refreshCache}
                    isLoading={refreshing}
                    title="Clear the local data cache and reload the latest data"
                >
                    Refresh data
                </Button>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                    <div className="flex flex-wrap gap-4 items-end">
                        <fieldset className="min-w-[200px]">
                            <legend className="ds-field__label mb-1">Period</legend>
                            <div className="flex flex-wrap items-center gap-2">
                                <Segmented value={preset} onChange={pickPreset} options={PRESETS.map((p) => [p.id, p.label])} label="Period" />
                                {preset === 'custom' ? (
                                    <>
                                        <Input type="date" aria-label="Start date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
                                        <Icon glyph={ICONS.arrowRight} size="sm" tone="muted" />
                                        <Input type="date" aria-label="End date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
                                    </>
                                ) : (
                                    <span className="text-xs text-gray-500 tabular-nums">{periodWindowText(range)}</span>
                                )}
                            </div>
                        </fieldset>
                        <Field label="Branch" className="min-w-[170px]">
                            <Select value={branch} onChange={(e) => setBranch(e.target.value)} options={HOME_BRANCHES} />
                        </Field>
                    </div>
                    <AskYelo />
                    <OverviewBoardView filters={filters} overview={overview} attentionPanel={attentionCard} showHeading={false} />
            </div>

            <style>{`
                .dash-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: var(--space-3); align-items: start; }
                .dash-attn { list-style: none; }
                .dash-attn__item { display: flex; align-items: center; gap: var(--space-3); padding: 10px var(--space-4); border-bottom: 1px solid var(--border); }
                .dash-attn__group { border-bottom: 1px solid var(--border); }
                .dash-attn__group:last-child { border-bottom: 0; }
                .dash-attn__group .dash-attn__item { border-bottom: 0; }
                .dash-attn__actions { display: inline-flex; gap: var(--space-2); flex: none; }
                .dash-case-list { list-style: none; margin: 0 var(--space-4) var(--space-3); border: 1px solid var(--border); border-radius: var(--radius-md); }
                .dash-case { display: flex; align-items: center; gap: var(--space-3); padding: 8px 12px; border-bottom: 1px solid var(--border); flex-wrap: wrap; }
                .dash-case:last-child { border-bottom: 0; }
                .dash-case__text { display: flex; flex-direction: column; min-width: 0; flex: 1; }
                .dash-case__title { font-weight: 600; font-size: var(--text-sm); }
                .dash-case__reasons { display: inline-flex; flex-wrap: wrap; gap: 6px; }
                .dash-closed { border-top: 1px solid var(--border); padding: 8px var(--space-4); }
                .dash-closed__toggle { font-size: var(--text-xs); color: var(--text-3); background: none; border: 0; cursor: pointer; padding: 0; margin-bottom: 6px; }
                .dash-closed__toggle:hover { color: var(--accent-text); text-decoration: underline; }
                .dash-closed .dash-case-list { margin: 0; }
                .dash-attn__icon { width: 30px; height: 30px; border-radius: var(--radius-md); display: grid; place-items: center; flex: none; }
                .dash-attn__icon--danger { background: var(--danger-soft); color: var(--danger); }
                .dash-attn__icon--warning { background: var(--warning-soft); color: var(--warning); }
                .dash-attn__icon--success { background: var(--success-soft); color: var(--success); }
                .dash-clear { display: flex; align-items: center; gap: var(--space-3); padding: 10px var(--space-4); }
                .dash-clear__text { font-size: var(--text-sm); color: var(--text-2); }
                .dash-attn__text { display: flex; flex-direction: column; min-width: 0; flex: 1; }
                .dash-attn__title { font-weight: 500; font-size: var(--text-sm); }
                .dash-attn__sub { font-size: var(--text-xs); color: var(--text-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                @media (max-width: 900px) { .dash-grid { grid-template-columns: minmax(0, 1fr); } }
            `}</style>
        </Layout>
    );
};

export default Dashboard;
